import { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Grid,
    Alert,
    CircularProgress,
    IconButton,
    Tooltip,
    Stack,
    Divider,
    Avatar
} from "@mui/material";
import {
    ThumbUp,
    ThumbUpOutlined,
    FilterList,
    Sort,
    Search,
    Person,
    PersonOff,
    Schedule,
    CheckCircle
} from "@mui/icons-material";
import { useAuth0 } from "@auth0/auth0-react";
import { useDealContext } from "../../../deal/context";
import { useUserContext } from "../../../users/context";
import { IFeedback, FeedbackType, FeedbackStatus } from "../../../../types/database";
import ClientProvider from "../../../../modules/ClientProvider";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { AdvisorProfileSummary } from "../../../advisor/components/AdvisorProfileSummary";
import { AdvisorProfileImage } from "../../../advisor/components/AdvisorProfileImage";
import { CompanyAdvisorLink } from "../CompanyAdvisorLink";
dayjs.extend(relativeTime);

interface FeedbackWithHelpful extends IFeedback {
    is_helpful_by_user?: boolean;
}

type SortOption = 'newest' | 'oldest' | 'helpful';
type FilterOption = 'all' | FeedbackType;

export const FeedbackList = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { dealContext } = useDealContext();
    const { userContext } = useUserContext();
    const [feedback, setFeedback] = useState<FeedbackWithHelpful[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState<SortOption>('newest');
    const [filterBy, setFilterBy] = useState<FilterOption>('all');
    const [submittingHelpful, setSubmittingHelpful] = useState<Set<number>>(new Set());

    const projectFeedbackClient = ClientProvider.provideProjectFeedbackClient();
    const project = dealContext.project;
    const viewerRole = dealContext.role;
    const currentUserId = userContext.dbUser?.id;

    // Helper function to check if current user has marked feedback as helpful
    const isMarkedHelpfulByUser = (helpfulMarks?: any[]) => {
        if (!currentUserId || !helpfulMarks) return false;
        return helpfulMarks.some(mark => mark.user?.id === currentUserId);
    };

    const fetchFeedback = async () => {
        if (!project) return;

        setLoading(true);
        try {
            const accessToken = await getAccessTokenSilently();
            const { data, error } = await projectFeedbackClient.getProjectFeedback(project.tag, accessToken);

            if (error) {
                setError(error.message || "Failed to load feedback");
                return;
            }

            if (data?.feedback) {
                // Add helpful status using existing helpful_marks data
                const feedbackWithHelpful = data.feedback.map((fb: IFeedback) => ({
                    ...fb,
                    is_helpful_by_user: isMarkedHelpfulByUser(fb.helpful_marks)
                }));
                setFeedback(feedbackWithHelpful);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "An unexpected error occurred");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleHelpful = async (feedbackId: number, isCurrentlyHelpful: boolean) => {
        if (!project) return;

        setSubmittingHelpful(prev => new Set(prev).add(feedbackId));

        try {
            const accessToken = await getAccessTokenSilently();

            if (isCurrentlyHelpful) {
                await projectFeedbackClient.removeHelpful(project.tag, feedbackId, accessToken);
            } else {
                await projectFeedbackClient.markAsHelpful(project.tag, feedbackId, accessToken);
            }

            // Update local state
            setFeedback(prev => prev.map(fb =>
                fb.id === feedbackId
                    ? {
                        ...fb,
                        is_helpful_by_user: !isCurrentlyHelpful,
                        helpful_count: isCurrentlyHelpful ? fb.helpful_count - 1 : fb.helpful_count + 1
                    }
                    : fb
            ));
        } catch (err) {
            console.error("Failed to toggle helpful status:", err);
        } finally {
            setSubmittingHelpful(prev => {
                const newSet = new Set(prev);
                newSet.delete(feedbackId);
                return newSet;
            });
        }
    };

    const filteredAndSortedFeedback = feedback
        .filter(fb => {
            // Filter by type
            if (filterBy !== 'all' && fb.type !== filterBy) return false;

            // Filter by search term
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                return (
                    fb.title.toLowerCase().includes(searchLower) ||
                    fb.content.toLowerCase().includes(searchLower) ||
                    fb.advisor.user?.name?.toLowerCase().includes(searchLower) ||
                    fb.tags?.some(tag => tag.toLowerCase().includes(searchLower))
                );
            }

            return true;
        })
        .sort((a, b) => {
            switch (sortBy) {
                case 'newest':
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                case 'oldest':
                    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                case 'helpful':
                    return b.helpful_count - a.helpful_count;
                default:
                    return 0;
            }
        });

    const getStatusColor = (status: FeedbackStatus) => {
        switch (status) {
            case FeedbackStatus.DRAFT:
                return 'default';
            case FeedbackStatus.SUBMITTED:
                return 'primary';
            case FeedbackStatus.REVIEWED:
                return 'success';
            case FeedbackStatus.ARCHIVED:
                return 'secondary';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status: FeedbackStatus) => {
        switch (status) {
            case FeedbackStatus.SUBMITTED:
                return <Schedule fontSize="small" />;
            case FeedbackStatus.REVIEWED:
                return <CheckCircle fontSize="small" />;
            default:
                return undefined;
        }
    };

    const getPriorityColor = (priority?: number) => {
        if (!priority) return 'default';
        if (priority >= 4) return 'error';
        if (priority >= 3) return 'warning';
        return 'success';
    };

    useEffect(() => {
        fetchFeedback();
    }, [project]);

    if (!project) {
        return <Alert severity="error">Project not found</Alert>;
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Filters and Search */}
            <Box sx={{ mb: 3, borderBottom: "1px solid #c9c9c9" }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                placeholder="Search feedback..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Filter by Type</InputLabel>
                                <Select
                                    value={filterBy}
                                    onChange={(e) => setFilterBy(e.target.value as FilterOption)}
                                    label="Filter by Type"
                                    startAdornment={<FilterList sx={{ mr: 1 }} />}
                                >
                                    <MenuItem value="all">All Types</MenuItem>
                                    <MenuItem value={FeedbackType.GENERAL}>General</MenuItem>
                                    <MenuItem value={FeedbackType.PRODUCT}>Product</MenuItem>
                                    <MenuItem value={FeedbackType.STRATEGY}>Strategy</MenuItem>
                                    <MenuItem value={FeedbackType.MARKET}>Market</MenuItem>
                                    <MenuItem value={FeedbackType.OPERATIONS}>Operations</MenuItem>
                                    <MenuItem value={FeedbackType.FUNDRAISING}>Fundraising</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Sort by</InputLabel>
                                <Select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                                    label="Sort by"
                                    startAdornment={<Sort sx={{ mr: 1 }} />}
                                >
                                    <MenuItem value="newest">Newest First</MenuItem>
                                    <MenuItem value="oldest">Oldest First</MenuItem>
                                    <MenuItem value="helpful">Most Helpful</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </CardContent>
            </Box>

            {/* Feedback Stats */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    Showing {filteredAndSortedFeedback.length} of {feedback.length} feedback items
                </Typography>
            </Box>

            {/* Feedback List */}
            {filteredAndSortedFeedback.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No feedback found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {searchTerm || filterBy !== 'all'
                                ? "Try adjusting your search or filters"
                                : "No advisor feedback has been submitted yet"
                            }
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={3}>
                    {filteredAndSortedFeedback.map((fb) => (
                        <Grid item xs={12} md={6} lg={4} key={fb.id}>
                            <Card sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow: "0 0 5px #c9c9c9",
                                '&:hover': { boxShadow: 2 }
                            }}>
                                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                    {/* Header */}
                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Box>
                                                <Typography variant="h6" sx={{
                                                    fontSize: '1.1rem',
                                                    lineHeight: 1.3,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    flex: 1,
                                                    mr: 1
                                                }}>
                                                    {fb.title}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                    {dayjs(fb.created_at).fromNow()}
                                                </Typography>
                                            </Box>
                                            {fb.is_anonymous ? (
                                                <Tooltip title="Anonymous">
                                                    <Box>
                                                        <AdvisorProfileImage advisor={null} size="mini" />
                                                    </Box>
                                                </Tooltip>

                                            ) : (
                                                <CompanyAdvisorLink uuid={fb.advisor.uuid}>
                                                    <Box>
                                                        <AdvisorProfileImage advisor={fb.advisor} size="mini" />
                                                    </Box>
                                                </CompanyAdvisorLink>
                                            )}
                                        </Box>

                                    </Box>

                                    {/* Content */}
                                    <Typography variant="body2" sx={{
                                        mb: 2,
                                        flexGrow: 1,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 4,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        fontSize: '0.9rem',
                                        lineHeight: 1.4
                                    }}>
                                        {fb.content}
                                    </Typography>

                                    {/* Actions */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                                        <Chip
                                            label={fb.type.charAt(0).toUpperCase() + fb.type.slice(1)}
                                            color="default"
                                            variant="filled"
                                            size="small"
                                        />
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                {fb.helpful_count} helpful
                                            </Typography>
                                            <Tooltip title={fb.is_helpful_by_user ? "Remove helpful" : "Mark as helpful"}>
                                                <IconButton
                                                    onClick={() => handleToggleHelpful(fb.id, fb.is_helpful_by_user || false)}
                                                    disabled={submittingHelpful.has(fb.id)}
                                                    color={fb.is_helpful_by_user ? "primary" : "default"}
                                                    size="small"
                                                >
                                                    {submittingHelpful.has(fb.id) ? (
                                                        <CircularProgress size={16} />
                                                    ) : fb.is_helpful_by_user ? (
                                                        <ThumbUp fontSize="small" />
                                                    ) : (
                                                        <ThumbUpOutlined fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};